import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import AlertMessage from "../../common/actions/AlertMessage";
import Loader from "../../common/loader/Loader";
import SectionTitle from "../../common/shared/SectionTitle";
import {
  applyNfcCard,
  getUserNfcCards,
  updateNfcCardBankDetails,
  uploadNfcCardPhoto,
} from "../../redux/actions/nfc_card_action";
import NfcApplicationForm from "./components/NfcApplicationForm";
import NfcApplicationSummaryCard from "./components/NfcApplicationSummaryCard";
import {
  bankDetailFields,
  createFormData,
  emptyFormData,
} from "./nfcCardConstants";

export { bankDetailFields, createFormData, emptyFormData };

const link = [{ name: "NFC Identification Card" }];

function NfcCard() {
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [formData, setFormData] = useState(emptyFormData);
  const [passportPhoto, setPassportPhoto] = useState(null);
  const [currentCard, setCurrentCard] = useState(null);
  const [editingApplication, setEditingApplication] = useState(null);
  const [autoPayCardId, setAutoPayCardId] = useState(null);

  const dispatch = useDispatch();
  const location = useLocation();
  const routePrefix = location.pathname.startsWith("/mobile") ? "/mobile" : "/home";
  const { data, card, nfcCardError } = useSelector((state) => state.nfcCard);
  const userData = useSelector((state) => state.auth?.userData);
  const storedUser = JSON.parse(localStorage.getItem("profile") || "{}")?.user;
  const user = userData || storedUser;
  const isInGoodStanding = user?.standing === true;

  const unpaidApplication = useMemo(
    () => data?.find((item) => item.paid !== 1),
    [data],
  );

  const applications = useMemo(() => {
    const latestCard = currentCard || card;
    const existingApplications = data || [];

    if (!latestCard?.id) {
      return existingApplications;
    }

    const hasLatestCard = existingApplications.some(
      (item) => item.id === latestCard.id,
    );

    if (!hasLatestCard) {
      return [latestCard, ...existingApplications];
    }

    return existingApplications.map((item) =>
      item.id === latestCard.id ? latestCard : item,
    );
  }, [card, currentCard, data]);

  const canApply = applications.length === 0;
  const showApplicationForm =
    (isInGoodStanding &&
      (canApply || (editingApplication && editingApplication.paid !== 1))) ||
    editingApplication?.paid === 1;

  const updateFormValue = ({ updateType, value }) => {
    setFormData((current) => ({ ...current, [updateType]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.ghana_card_number && editingApplication?.paid !== 1) {
      toast.error("Please enter your Ghana Card number.");
      return;
    }

    const missingBankDetail = bankDetailFields.some(
      (field) => !formData[field]?.trim(),
    );
    if (missingBankDetail) {
      toast.error("Please complete all bank account details.");
      return;
    }

    if (
      editingApplication?.paid !== 1 &&
      !passportPhoto &&
      !editingApplication?.passport_photo
    ) {
      toast.error("Please upload your passport-sized picture.");
      return;
    }

    setSubmitLoading(true);

    if (editingApplication?.paid === 1) {
      const bankDetails = bankDetailFields.reduce(
        (values, field) => ({ ...values, [field]: formData[field] }),
        {},
      );

      dispatch(updateNfcCardBankDetails(editingApplication.id, bankDetails))
        .then((response) => {
          const responseCard = response?.data?.card;
          if (responseCard) {
            setCurrentCard(responseCard);
            setEditingApplication(null);
            setFormData(createFormData());
            toast.success("Bank account details updated.");
          }
          return response;
        })
        .finally(() => setSubmitLoading(false));
      return;
    }

    dispatch(applyNfcCard(formData))
      .then((response) => {
        const responseCard = response?.data?.card;
        if (!responseCard) {
          return response;
        }

        setCurrentCard(responseCard);

        if (!passportPhoto) {
          setPassportPhoto(null);
          setEditingApplication(null);
          return response;
        }

        const fileData = new FormData();
        fileData.append("file", passportPhoto);

        return dispatch(uploadNfcCardPhoto(responseCard.id, fileData)).then(
          (uploadResponse) => {
            const uploadedCard = uploadResponse?.data?.card;
            if (uploadedCard) {
              setCurrentCard(uploadedCard);
              if (!editingApplication) {
                setAutoPayCardId(uploadedCard.id);
              }
            }
            setPassportPhoto(null);
            setEditingApplication(null);
            return uploadResponse;
          },
        );
      })
      .finally(() => setSubmitLoading(false));
  };

  const handleEdit = (item) => {
    setEditingApplication(item);
    setPassportPhoto(null);
    setFormData(createFormData(item));
  };

  const handleCancelEdit = () => {
    setEditingApplication(null);
    setPassportPhoto(null);
    setFormData(emptyFormData);
  };

  useEffect(() => {
    dispatch(getUserNfcCards()).finally(() => setLoading(false));
  }, [dispatch]);

  useEffect(() => {
    if (unpaidApplication) {
      setFormData(createFormData(unpaidApplication));
    }
  }, [unpaidApplication]);

  return (
    <>
      <SectionTitle
        title="NFC Identification Card"
        description=""
        children={link}
        link={routePrefix}
      />

      <div className="my-8 max-w-[1200px] m-auto px-4">
        {nfcCardError && <AlertMessage type="error" message={nfcCardError} />}

        {loading ? (
          <Loader />
        ) : (
          <>
            {!isInGoodStanding && (
              <AlertMessage
                type="error"
                message="Please be in good financial standing to apply for an NFC card."
              />
            )}

            {showApplicationForm && (
              <NfcApplicationForm
                formData={formData}
                submitLoading={submitLoading}
                editingApplication={editingApplication}
                onSubmit={handleSubmit}
                onCancel={handleCancelEdit}
                onFileChange={setPassportPhoto}
                onFieldChange={updateFormValue}
              />
            )}

            {!applications.length
              ? null
              : applications.map((item) => (
                <NfcApplicationSummaryCard
                  key={item.id}
                  application={item}
                  isInGoodStanding={isInGoodStanding}
                  autoPayCardId={autoPayCardId}
                  onEdit={handleEdit}
                  onAutoPayReset={() => setAutoPayCardId(null)}
                />
              ))}
          </>
        )}
      </div>
    </>
  );
}

export default NfcCard;
